import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, FileText, LogOut, Search, Eye, 
  CheckCircle, Clock, AlertCircle, Home,
  ChevronRight, Download, Filter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [applications, setApplications] = useState([]);
  const [contactForms, setContactForms] = useState([]);
  const [agentApplications, setAgentApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('applications');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/portal/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [appsRes, contactsRes, agentsRes] = await Promise.all([
        axios.get(`${API}/admin/applications`, { withCredentials: true }),
        axios.get(`${API}/contact`, { withCredentials: true }),
        axios.get(`${API}/agent-application`, { withCredentials: true })
      ]);
      setApplications(appsRes.data);
      setContactForms(contactsRes.data);
      setAgentApplications(agentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/portal/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_review: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      documents_needed: 'bg-orange-100 text-orange-800'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[status] || 'bg-slate-100 text-slate-800'}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.full_legal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    approved: applications.filter(a => a.status === 'approved').length,
    needsDocs: applications.filter(a => a.status === 'documents_needed').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-amber-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-navy text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_insurance-guide-8/artifacts/nhiwlosf_image.png" 
                alt="WealthConnexs"
                className="h-10"
              />
              <span className="font-semibold">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-300 hover:text-white">
                <Home className="w-5 h-5" />
              </Link>
              <span className="text-sm text-slate-300">{user?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Applications', value: stats.total, icon: Users, color: 'bg-blue-500' },
            { label: 'Pending Review', value: stats.pending, icon: Clock, color: 'bg-yellow-500' },
            { label: 'Approved', value: stats.approved, icon: CheckCircle, color: 'bg-green-500' },
            { label: 'Documents Needed', value: stats.needsDocs, icon: AlertCircle, color: 'bg-orange-500' }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy">{stat.value}</p>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'applications', label: 'Customer Applications', count: applications.length },
            { id: 'contacts', label: 'Contact Forms', count: contactForms.length },
            { id: 'agents', label: 'Agent Applications', count: agentApplications.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'bg-navy text-white' 
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Customer Applications Tab */}
        {activeTab === 'applications' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-100"
          >
            {/* Filters */}
            <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="documents_needed">Documents Needed</option>
              </select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Applicant</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">App ID</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Policy Interest</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Documents</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredApplications.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        No applications found
                      </td>
                    </tr>
                  ) : (
                    filteredApplications.map((app) => (
                      <tr key={app.user_id} className="hover:bg-slate-50" data-testid={`app-row-${app.user_id}`}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-900">{app.full_legal_name}</p>
                            <p className="text-sm text-slate-500">{app.user?.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{app.application_id}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {app.policy_interests?.slice(0, 2).join(', ')}
                          {app.policy_interests?.length > 2 && '...'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-sm">
                            <FileText className="w-4 h-4 text-slate-400" />
                            {app.documents?.length || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(app.status)}</td>
                        <td className="px-4 py-3">
                          <Link to={`/admin/application/${app.user_id}`}>
                            <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700">
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Contact Forms Tab */}
        {activeTab === 'contacts' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-100"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Phone</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Service Interest</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Message</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {contactForms.map((contact) => (
                    <tr key={contact.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">{contact.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{contact.email}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{contact.phone || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{contact.service_interest || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">{contact.message}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {new Date(contact.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Agent Applications Tab */}
        {activeTab === 'agents' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-100"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Phone</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Location</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Experience</th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {agentApplications.map((agent) => (
                    <tr key={agent.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">{agent.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{agent.email}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{agent.phone}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{agent.location}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{agent.experience || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {new Date(agent.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
